import React, { useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import heroVideo from "../assets/hero.mp4";
import {
  FaUsers,
  FaHandshake,
  FaVideo,
  FaStore,
  FaRobot,
  FaChartPie,
  FaLink,
  FaComments,
} from "react-icons/fa";
import AIAssistant from "./AI_Implementation/AIAssistant/AIAssistant";
import HomepageSocialShowcase from "./HomepageSocialShowcase";
import FeatureShowcase from "./FeatureShowcase/FeatureShowcase";

const HomePage = () => {
  const videoRef = useRef(null);

  const handleMouseEnter = () => videoRef.current?.play();
  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const services = [
    {
      icon: <FaUsers className="w-8 h-8" />,
      title: "Advertising",
      description:
        "Earn more by sharing advertising reels with your followers. Get both fixed and variable payouts based on engagement.",
      color: "bg-orange-500",
    },
    {
      icon: <FaHandshake className="w-8 h-8" />,
      title: "Partner Marketing",
      description:
        "Monetize your Instagram presence through affiliate marketing. Earn commissions on every sale through your unique link.",
      color: "bg-blue-500",
    },
    {
      icon: <FaStore className="w-8 h-8" />,
      title: "Build Your Own Brand",
      description:
        "Launch your own brand with our strategic guidance. Create and sell products under your personal brand.",
      color: "bg-green-500",
    },
    {
      icon: <FaVideo className="w-8 h-8" />,
      title: "Video Production",
      description:
        "Professional video production and editing services to create high-quality, engaging content.",
      color: "bg-purple-500",
    },
  ];

  const instaTools = [
    {
      icon: <FaComments className="w-5 h-5 text-white" />,
      title: "Comment → DM Automation",
      description:
        "When someone comments your keyword, Infuber checks follow status and delivers your product link in DMs automatically.",
      color: "bg-orange-500",
    },
    {
      icon: <FaRobot className="w-5 h-5 text-white" />,
      title: "AI Comment Analytics",
      description:
        "Turn comments into clear insights — sentiment scores, audience persona, top themes, and next actions powered by AI.",
      color: "bg-blue-500",
    },
    {
      icon: <FaLink className="w-5 h-5 text-white" />,
      title: "Affiliate Link Tracking",
      description:
        "Generate short Infuber links, share them anywhere, and track visits, unique clicks, and conversions in real time.",
      color: "bg-green-500",
    },
    {
      icon: <FaChartPie className="w-5 h-5 text-white" />,
      title: "Creator Dashboard",
      description:
        "Manage automations, media, and earnings from one place — activate, edit, or analyze anytime.",
      color: "bg-purple-500",
    },
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />

      {/* Hero Section */}
      <div
        className="relative flex h-[80vh] w-full flex-col items-center justify-center overflow-hidden text-center"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-40 left-1/2 h-[32rem] w-[32rem] -translate-x-1/2 rounded-full bg-orange-500/15 blur-3xl" />
          <div className="absolute -bottom-40 left-1/3 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl" />
        </div>

        <video
          ref={videoRef}
          className="absolute left-0 top-0 h-full w-full object-cover"
          src={heroVideo}
          muted
          loop
        />

        <div className="absolute left-0 top-0 h-full w-full bg-gradient-to-b from-black/70 via-black/55 to-black/80" />

        <div className="relative z-10 px-4 text-center">
          <h2 className="text-2xl font-bold text-white/95 sm:text-3xl md:text-4xl">
            THE WORLD&apos;S LEADING, SOCIAL & INFLUENCER MARKETING AGENCY
          </h2>
          <h3 className="mt-2 text-lg font-bold text-white/70 sm:mt-4 sm:text-2xl md:text-3xl">
            POWERED BY CREATORS
          </h3>

          <div className="mt-6 flex flex-col items-center justify-center gap-4 sm:flex-row sm:items-start sm:gap-6">
            <Link to="/our-work">
              <button
                type="button"
                className="w-40 rounded-md bg-orange-500 px-3 py-2 font-bold text-white transition-colors hover:bg-orange-600 sm:w-48 md:px-6 md:py-3"
              >
                Our Services
              </button>
            </Link>
            <Link to="/about-infuber">
              <button
                type="button"
                className="w-40 rounded-md border border-white px-3 py-2 font-bold transition-colors hover:bg-white hover:text-black sm:w-48 md:px-6 md:py-3"
              >
                About Infuber
              </button>
            </Link>
          </div>
        </div>
      </div>

      <HomepageSocialShowcase />

      <FeatureShowcase />

      {/* Insta Tools Section — quick feature summary */}
      <section className="relative overflow-hidden bg-black px-4 py-8 md:py-12 lg:px-8">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-44 left-1/3 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-orange-500/10 blur-3xl" />
          <div className="absolute -bottom-44 left-2/3 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-7xl">
          <div className="mb-12 text-center md:mb-16">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-orange-400">
              Insta Tools
            </p>
            <h2 className="mb-4 text-2xl font-bold md:text-4xl">Grow with Insta Tools</h2>
            <p className="mx-auto max-w-3xl text-sm text-white/70 md:text-lg">
              Automate Instagram DMs, understand your audience with AI, and earn through trackable
              affiliate links — all from one dashboard.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 lg:grid-cols-4">
            {instaTools.map((tool) => (
              <div
                key={tool.title}
                className="rounded-2xl border border-white/10 bg-white/5 p-4 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)] backdrop-blur transition-transform duration-300 hover:scale-105 md:p-6"
              >
                <div className="mb-4 flex items-center gap-3">
                  <div
                    className={`${tool.color} flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg`}
                  >
                    {tool.icon}
                  </div>
                  <h4 className="mb-0 text-base font-semibold leading-snug md:text-lg">
                    {tool.title}
                  </h4>
                </div>
                <p className="mb-4 text-sm text-white/70 md:text-base">{tool.description}</p>
                <Link
                  to="/insta-tools/info"
                  className="font-semibold text-orange-500 transition hover:text-orange-400"
                >
                  Show →
                </Link>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center md:mt-12">
            <Link to="/insta-tools/info">
              <button
                type="button"
                className="rounded-md bg-orange-500 px-3 py-2 font-semibold text-white transition-colors hover:bg-orange-600 md:px-6 md:py-3"
              >
                Show Insta Tools
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="relative overflow-hidden bg-black px-4 py-20 sm:px-6 lg:px-8">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-40 left-1/2 h-[32rem] w-[32rem] -translate-x-1/2 rounded-full bg-orange-500/15 blur-3xl" />
          <div className="absolute -bottom-40 left-1/3 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-7xl">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-2xl font-bold md:text-4xl">About Infuber</h2>
            <p className="mx-auto max-w-3xl text-sm text-white/70 md:text-lg">
              Empowering brands and creators through innovative influencer marketing solutions
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)] backdrop-blur md:p-6">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex-shrink-0 text-orange-500">
                  <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                </div>
                <h3 className="mb-0 text-xl font-semibold">Secure Platform</h3>
              </div>
              <p className="text-sm text-white/70 md:text-lg">
                Enterprise-grade security with advanced authentication and data protection
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)] backdrop-blur md:p-6">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex-shrink-0 text-orange-500">
                  <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <h3 className="mb-0 text-xl font-semibold">Real-time Analytics</h3>
              </div>
              <p className="text-sm text-white/70 md:text-lg">
                Comprehensive insights and reporting tools for campaign optimization
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)] backdrop-blur md:p-6">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex-shrink-0 text-orange-500">
                  <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
                <h3 className="mb-0 text-xl font-semibold">Community Focus</h3>
              </div>
              <p className="text-sm text-white/70 md:text-lg">
                Build meaningful connections between brands and influencers
              </p>
            </div>
          </div>
          <div className="mt-6 text-center md:mt-12">
            <Link to="/about-infuber">
              <button
                type="button"
                className="rounded-md bg-orange-500 px-3 py-2 font-semibold text-white transition-colors hover:bg-orange-600 md:px-6 md:py-3"
              >
                Learn More About Us
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="relative overflow-hidden bg-black px-4 py-8 md:py-12 lg:px-8">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-44 left-1/3 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-orange-500/10 blur-3xl" />
          <div className="absolute -bottom-44 left-2/3 h-[30rem] w-[30rem] -translate-x-1/2 rounded-full bg-blue-500/10 blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-7xl">
          <div className="mb-16 text-center">
            <h2 className="mb-4 text-2xl font-bold md:text-4xl">Our Services</h2>
            <p className="mx-auto max-w-3xl text-sm text-white/70 md:text-lg">
              Comprehensive solutions for brands and creators to maximize their impact
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {services.map((service) => (
              <div
                key={service.title}
                className="transform rounded-2xl border border-white/10 bg-white/5 p-4 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)] backdrop-blur transition-transform duration-300 hover:scale-105 md:p-6"
              >
                <div className="mb-4 flex items-center gap-3">
                  <div
                    className={`${service.color} flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg`}
                  >
                    {service.icon}
                  </div>
                  <h4 className="mb-0 text-lg font-semibold">{service.title}</h4>
                </div>
                <p className="mb-4 text-sm text-white/70 md:text-lg">{service.description}</p>
                <Link
                  to="/our-work"
                  className="font-semibold text-orange-500 hover:text-orange-400"
                >
                  Learn More →
                </Link>
              </div>
            ))}
          </div>
          <div className="mt-6 text-center md:mt-12">
            <Link to="/our-work">
              <button
                type="button"
                className="rounded-md bg-orange-500 px-3 py-2 font-semibold text-white transition-colors hover:bg-orange-600 md:px-6 md:py-3"
              >
                View All Services
              </button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      <AIAssistant context="homepage" />
    </div>
  );
};

export default HomePage;
